<template>
  <div v-if="visible" class="oa-modal-overlay" @click.self="hide">
    <div class="oa-modal">
      <div class="oa-modal-header">
        <h3>
          <i class="fa fa-calendar-plus-o oa-modal-icon"></i>
          <span>{{ isEditing ? 'Edit Appointment Type' : 'Create New Appointment Type' }}</span>
        </h3>
        <button class="oa-modal-close" @click="hide">
          <i class="fa fa-times"></i>
        </button>
      </div>

      <div class="oa-modal-body">
        <!-- Name -->
        <div class="oa-form-group">
          <label class="oa-form-label">Appointment Type Name</label>
          <input type="text" class="oa-form-input" v-model="form.name" placeholder="New Patient Exam & Cleaning" />
        </div>

        <!-- Description -->
        <div class="oa-form-group">
          <label class="oa-form-label">Description</label>
          <textarea class="oa-form-textarea" v-model="form.description" rows="3" placeholder="Comprehensive exam and cleaning for new patients"></textarea>
        </div>

        <!-- Duration -->
        <div class="oa-form-group">
          <label class="oa-form-label">Appointment Duration (Minutes)</label>
          <div class="oa-duration-grid">
            <button
              v-for="d in durationOptions"
              :key="d"
              class="oa-duration-btn"
              :class="{ selected: form.duration === d }"
              @click="selectPresetDuration(d)"
            >
              {{ d }}
            </button>
          </div>
          <div class="oa-custom-duration">
            <label class="oa-custom-duration-label">Custom:</label>
            <input
              type="number"
              class="oa-custom-duration-input"
              v-model.number="customDuration"
              @input="onCustomDurationInput"
              min="5"
              max="480"
              placeholder="e.g. 40"
            />
            <span class="oa-custom-duration-unit">minutes</span>
          </div>
          <p class="oa-form-help">Selected: {{ form.duration }} minutes</p>
        </div>

        <!-- New Patients vs. Existing Patients -->
        <div class="oa-form-group">
          <label class="oa-form-label">New Patients vs. Existing Patients</label>
          <p class="oa-form-help">Make this appointment type available for:</p>
          <div class="oa-options-list">
            <div class="oa-checkbox-item">
              <input type="checkbox" id="modal-newPatients" v-model="form.availableFor.newPatients" />
              <label for="modal-newPatients">
                <span>New Patients</span>
              </label>
            </div>
            <div class="oa-checkbox-item">
              <input type="checkbox" id="modal-existingPatients" v-model="form.availableFor.existingPatients" />
              <label for="modal-existingPatients">
                <span>Existing Patients</span>
              </label>
            </div>
          </div>
        </div>

        <!-- Post-Booking Redirect URL (per appointment type) -->
        <div class="oa-form-group">
          <label class="oa-form-label">Post-Booking Redirect URL</label>
          <input type="url" class="oa-form-input" v-model.trim="form.thankYouUrl" placeholder="https://yourwebsite.com/thank-you-aligners" />
          <p class="oa-form-help">Override the general redirect URL for this appointment type. Leave blank to use the general setting (or the built-in confirmation screen). UTM parameters will be forwarded automatically.</p>
        </div>

        <!-- Procedure Codes -->
        <div class="oa-form-group">
          <label class="oa-form-label">Procedure/Appointment Codes</label>
          <p class="oa-form-help">Please add related procedure codes (e.g., D1110 for Adult Prophy, D0150 for Comprehensive Exam).</p>
          <div class="oa-warning-box">
            <p>
              <i class="fa fa-exclamation-triangle"></i>
              <strong>IMPORTANT:</strong> Procedure codes you select will be added to the Appointment Note in Open Dental, but will NOT be attached to the actual appointment.
            </p>
          </div>
          <multiselect
            v-model="form.selectedProcedureCodes"
            :options="procedureCodes"
            :multiple="true"
            :custom-label="codeWithDesc"
            label="ProcCode"
            track-by="ProcCode"
            placeholder="Search and select procedure codes..."
            :close-on-select="false"
          />
        </div>

        <!-- Operatories & Providers -->
        <div class="oa-form-group">
          <label class="oa-form-label">
            Operatories & Providers: <span class="oa-required-indicator">*Required</span>
          </label>
          <p class="oa-form-help">For each operatory, select the provider who can perform this appointment type. <strong>At least one operatory/provider combination is required.</strong></p>
          <div class="oa-operatory-list" :class="{ 'oa-operatory-list-error': showOperatoryError }">
            <div v-for="op in operatories" :key="op.OpNum" class="oa-operatory-item">
              <div class="oa-operatory-info">
                <strong>{{ op.OpName || ('Operatory ' + op.OpNum) }}</strong>
                <span class="oa-form-help">Select the provider for this operatory:</span>
              </div>
              <select class="oa-operatory-select" v-model="form.operatoryProviderMap[op.OpNum]">
                <option :value="null">— Select Provider —</option>
                <option v-for="prov in providers" :key="prov.ProvNum" :value="prov.ProvNum">
                  {{ prov.FName }} {{ prov.LName }}
                </option>
              </select>
            </div>
            <p v-if="!operatories || operatories.length === 0" class="oa-form-help">
              No operatories found. Operatories are loaded from your Open Dental settings.
            </p>
          </div>
          <p v-if="showOperatoryError" class="oa-error-text">
            <i class="fa fa-exclamation-circle"></i> Please select at least one operatory/provider combination.
          </p>
        </div>

        <!-- Additional Options -->
        <div class="oa-form-group">
          <label class="oa-form-label">Additional Options:</label>
          <div class="oa-options-list">
            <div class="oa-checkbox-item">
              <input type="checkbox" id="modal-allowDoubleBooking" v-model="form.allowDoubleBooking" />
              <label for="modal-allowDoubleBooking">
                <span>Allow Double Booking</span>
                <p class="oa-checkbox-help">When on, this provider can be booked in two operatories at the same time (for example a hygienist seating a patient in one room while finishing in another). Each operatory still has to be free. When off, a time is offered only when both the operatory and the provider are free.</p>
              </label>
            </div>
            <div class="oa-checkbox-item">
              <input type="checkbox" id="modal-hideService" v-model="form.hideService" />
              <label for="modal-hideService">
                <span>Hide Service from Patients</span>
                <p class="oa-checkbox-help">This appointment type will not be visible to patients booking online</p>
              </label>
            </div>
            <div class="oa-checkbox-item">
              <input type="checkbox" id="modal-blockScheduling" v-model="form.blockScheduling" />
              <label for="modal-blockScheduling">
                <span>Block Scheduling</span>
                <p class="oa-checkbox-help">Allow scheduling only in specific blocks</p>
              </label>
            </div>

            <div v-if="form.blockScheduling" class="oa-block-dropdown">
              <label class="oa-form-label" style="font-size: 0.75rem;">Please select a single block from below:</label>
              <select class="oa-form-input" v-model="form.selectedBlock">
                <option :value="null" disabled>Select a block</option>
                <option v-for="bt in blockoutTypes" :key="bt.DefNum" :value="bt.DefNum">
                  {{ bt.ItemName }}
                </option>
              </select>
              <p class="oa-form-help">Only appointments in the selected block will be available for this service</p>
            </div>
          </div>
        </div>
      </div>

      <div class="oa-modal-footer">
        <button class="oa-btn oa-btn-secondary" @click="hide" :disabled="saving">Cancel</button>
        <button class="oa-btn oa-btn-primary" @click="save" :disabled="saving">
          <i v-if="saving" class="fa fa-spinner fa-spin" style="margin-right: 6px;"></i>
          {{ saving ? 'Saving...' : 'Save Appointment Type' }}
        </button>
      </div>

      <!-- Saving overlay -->
      <div v-if="saving" class="oa-modal-saving-overlay">
        <div class="oa-modal-saving-content">
          <i class="fa fa-spinner fa-spin"></i>
          <span>Saving appointment type...</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import Multiselect from 'vue-multiselect';

export default {
  name: 'AppointmentTypeModal',
  components: { Multiselect },
  props: {
    procedureCodes: { type: Array, default: () => [] },
    providers: { type: Array, default: () => [] },
    operatories: { type: Array, default: () => [] },
    blockoutTypes: { type: Array, default: () => [] },
    editType: { type: Object, default: null },
    saving: { type: Boolean, default: false }
  },

  data() {
    return {
      visible: false,
      durationOptions: [15, 30, 45, 60, 75, 90, 105, 120],
      customDuration: null,
      form: this.getEmptyForm(),
      showOperatoryError: false
    };
  },

  computed: {
    isEditing() {
      return this.editType !== null;
    }
  },

  watch: {
    editType(newVal) {
      if (newVal) {
        this.populateFromEditType(newVal);
      } else {
        this.form = this.getEmptyForm();
        this.customDuration = null;
      }
    },
    // Close modal when save completes (saving transitions true → false)
    saving(newVal, oldVal) {
      if (oldVal === true && newVal === false && this.visible) {
        this.hide();
      }
    }
  },

  methods: {
    selectPresetDuration(d) {
      this.form.duration = d;
      this.customDuration = null;
    },
    onCustomDurationInput() {
      const val = parseInt(this.customDuration);
      if (val && val >= 5) {
        this.form.duration = val;
      }
    },
    getEmptyForm() {
      return {
        name: '',
        description: '',
        duration: 30,
        selectedProcedureCodes: [],
        operatoryProviderMap: {},
        allowDoubleBooking: false,
        hideService: false,
        blockScheduling: false,
        selectedBlock: null,
        availableFor: { newPatients: true, existingPatients: true },
        thankYouUrl: ''
      };
    },

    show() {
      this.visible = true;
    },

    hide() {
      this.visible = false;
      this.form = this.getEmptyForm();
      this.customDuration = null;
      this.showOperatoryError = false;
    },

    codeWithDesc(code) {
      if (!code) return '';
      return `${code.ProcCode} — [${code.Descript || ''}]`;
    },

    populateFromEditType(type) {
      this.form.name = type.name || '';
      this.form.description = type.description || '';
      this.form.duration = type.duration || 30;
      this.customDuration = this.durationOptions.includes(this.form.duration) ? null : this.form.duration;
      this.form.allowDoubleBooking = type.allowDoubleBooking || false;
      this.form.hideService = type.hideService || false;
      this.form.blockScheduling = type.blockScheduling || false;
      this.form.selectedBlock = type.selectedBlock || null;
      this.form.availableFor = type.availableFor
        ? { ...type.availableFor }
        : { newPatients: true, existingPatients: true };
      this.form.thankYouUrl = type.thankYouUrl || '';

      // Restore procedure codes
      this.form.selectedProcedureCodes = [];
      if (type.procedureCodes && Array.isArray(type.procedureCodes)) {
        type.procedureCodes.forEach(code => {
          const codeStr = typeof code === 'string' ? code : code.ProcCode;
          const found = this.procedureCodes.find(pc => pc.ProcCode === codeStr);
          if (found) {
            this.form.selectedProcedureCodes.push(found);
          }
        });
      }

      // Restore operatory-provider map
      this.form.operatoryProviderMap = {};
      if (type.operatoryProviders && Array.isArray(type.operatoryProviders)) {
        type.operatoryProviders.forEach(op => {
          this.form.operatoryProviderMap[op.operatoryNum] = op.providerNum;
        });
      }
    },

    save() {
      // Reset error state
      this.showOperatoryError = false;

      if (!this.form.name || !this.form.name.trim()) {
        alert('Please enter an appointment type name.');
        return;
      }

      if (!this.form.duration || this.form.duration < 5) {
        alert('Please enter a valid appointment duration (minimum 5 minutes).');
        return;
      }

      let normalizedThankYouUrl = '';
      if (this.form.thankYouUrl) {
        let url = this.form.thankYouUrl.trim();
        if (!/^https?:\/\//i.test(url)) {
          url = 'https://' + url;
        }
        try {
          new URL(url);
        } catch (e) {
          alert('Post-Booking Redirect URL is not a valid URL. Example: https://yourwebsite.com/thank-you');
          return;
        }
        normalizedThankYouUrl = url;
        this.form.thankYouUrl = url;
      }

      const operatoryProviders = [];
      Object.keys(this.form.operatoryProviderMap).forEach(opNum => {
        const provNum = this.form.operatoryProviderMap[opNum];
        if (provNum) {
          const op = this.operatories.find(o => String(o.OpNum) === String(opNum));
          const prov = this.providers.find(p => p.ProvNum === provNum);
          operatoryProviders.push({
            operatoryNum: parseInt(opNum),
            operatoryName: op ? (op.OpName || 'Operatory ' + opNum) : 'Operatory ' + opNum,
            providerNum: provNum,
            providerName: prov ? `${prov.FName} ${prov.LName}` : '',
            isSecondary: prov ? (prov.IsSecondary || 0) : 0
          });
        }
      });

      // Validate: require at least one operatory/provider combination
      if (operatoryProviders.length === 0) {
        this.showOperatoryError = true;
        return;
      }

      const procedureCodes = this.form.selectedProcedureCodes.map(code =>
        typeof code === 'string' ? code : code.ProcCode
      );

      const appointmentType = {
        id: this.editType ? this.editType.id : `apt-${Date.now()}`,
        name: this.form.name.trim(),
        description: this.form.description.trim(),
        duration: this.form.duration,
        procedureCodes: procedureCodes,
        operatoryProviders: operatoryProviders,
        allowDoubleBooking: this.form.allowDoubleBooking,
        hideService: this.form.hideService,
        blockScheduling: this.form.blockScheduling,
        selectedBlock: this.form.blockScheduling ? this.form.selectedBlock : null,
        availableFor: { ...this.form.availableFor },
        thankYouUrl: normalizedThankYouUrl
      };

      this.$emit('saved', appointmentType);
      // Modal stays open during save — parent will close it via the saving prop watcher
    }
  }
};
</script>
